import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, MapPin, Calendar, Clock, Search, X, Zap,
  Share2, ChevronLeft, Users, Slash, Award
} from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../utils/axios';
import { format, parseISO, isToday, isTomorrow, differenceInDays } from 'date-fns';

interface Event {
  id: number;
  title: string;
  description?: string;
  event_date: string;
  event_time?: string;
  location?: string;
  category: string;
  points: number;
  capacity?: number;
  brochure_url?: string;
  status: 'upcoming' | 'ongoing' | 'completed';
  attendanceCount?: number;
  my_rsvp?: 'confirmed' | 'declined' | 'interested' | null;
  rsvp_confirmed?: number;
  rsvp_interested?: number;
}

const CATEGORIES = [
  { id: 'all',               label: 'All',         emoji: '✦', accent: '#0D5128' },
  { id: 'upcoming_filter',   label: 'Upcoming',    emoji: '📅', accent: '#2563eb' },
  { id: 'workshop',          label: 'Workshop',    emoji: '🛠',  accent: '#c2410c' },
  { id: 'seminar',           label: 'Seminar',     emoji: '🎓', accent: '#4f46e5' },
  { id: 'social',            label: 'Social',      emoji: '🎉', accent: '#db2777' },
  { id: 'sports',            label: 'Sports',      emoji: '⚡', accent: '#0d9488' },
  { id: 'community_service', label: 'Community',   emoji: '🤝', accent: '#b45309' },
  { id: 'fundraising',       label: 'Fundraising', emoji: '💛', accent: '#dc2626' },
];

const CAT_GRADIENTS: Record<string, string> = {
  workshop:          'linear-gradient(145deg, #7c2d12 0%, #c2410c 50%, #fb923c 100%)',
  seminar:           'linear-gradient(145deg, #1e1b4b 0%, #4338ca 50%, #818cf8 100%)',
  social:            'linear-gradient(145deg, #500724 0%, #be185d 50%, #f472b6 100%)',
  sports:            'linear-gradient(145deg, #042f2e 0%, #0f766e 50%, #2dd4bf 100%)',
  community_service: 'linear-gradient(145deg, #451a03 0%, #b45309 50%, #fbbf24 100%)',
  fundraising:       'linear-gradient(145deg, #450a0a 0%, #b91c1c 50%, #f87171 100%)',
  other:             'linear-gradient(145deg, #0f172a 0%, #1e3a5f 50%, #3b82f6 100%)',
  default:           'linear-gradient(145deg, #041e0e 0%, #0D5128 50%, #3daa75 100%)',
};

const STATUS_CFG = {
  upcoming:  { label: 'Upcoming', color: '#3b82f6', bg: 'rgba(59,130,246,0.18)',  glow: false },
  ongoing:   { label: 'Live Now', color: '#22c55e', bg: 'rgba(34,197,94,0.18)',   glow: true  },
  completed: { label: 'Ended',    color: '#94a3b8', bg: 'rgba(148,163,184,0.18)', glow: false },
};

function smartDate(dateStr: string): string {
  try {
    const d = parseISO(dateStr);
    if (isToday(d)) return 'Today';
    if (isTomorrow(d)) return 'Tomorrow';
    const diff = differenceInDays(d, new Date());
    if (diff > 0 && diff < 7) return `In ${diff}d`;
    if (diff < 0 && diff > -7) return `${Math.abs(diff)}d ago`;
    return format(d, 'MMM d');
  } catch { return dateStr; }
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const MemberEventsPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeStatus, setActiveStatus] = useState<'all' | 'upcoming' | 'ongoing' | 'completed'>('upcoming');
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState<Record<number, boolean>>({});
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const catScrollRef = useRef<HTMLDivElement>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (activeCategory !== 'all' && activeCategory !== 'upcoming_filter') params.category = activeCategory;
      if (activeCategory === 'upcoming_filter') params.status = 'upcoming';
      else if (activeStatus !== 'all') params.status = activeStatus;
      if (search.trim()) params.search = search.trim();

      const res = await axios.get('/events', { params });
      const raw = res.data?.data?.events ?? res.data?.events ?? res.data?.data ?? [];
      setEvents(Array.isArray(raw) ? raw : []);
    } catch {
      toast.error('Could not load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, activeStatus, search]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  useEffect(() => {
    if (searchOpen) setTimeout(() => searchRef.current?.focus(), 80);
  }, [searchOpen]);

  const handleRSVP = async (eventId: number, action: 'confirmed' | 'interested' | 'declined') => {
    if (rsvpLoading[eventId]) return;

    const userData = localStorage.getItem('user');
    if (!userData) { toast.error('Please log in again'); return; }
    const user = JSON.parse(userData);

    const ev = events.find(e => e.id === eventId);
    const isSame = ev?.my_rsvp === action;

    setRsvpLoading(p => ({ ...p, [eventId]: true }));

    // Optimistic update
    setEvents(prev => prev.map(e => {
      if (e.id !== eventId) return e;
      const prevRsvp = e.my_rsvp;
      const newRsvp = isSame ? null : action;
      return {
        ...e,
        my_rsvp: newRsvp,
        rsvp_confirmed: action === 'confirmed'
          ? Math.max(0, (e.rsvp_confirmed ?? 0) + (isSame ? -1 : prevRsvp === 'confirmed' ? 0 : 1))
          : prevRsvp === 'confirmed' ? Math.max(0, (e.rsvp_confirmed ?? 0) - 1) : e.rsvp_confirmed,
        rsvp_interested: action === 'interested'
          ? Math.max(0, (e.rsvp_interested ?? 0) + (isSame ? -1 : prevRsvp === 'interested' ? 0 : 1))
          : prevRsvp === 'interested' ? Math.max(0, (e.rsvp_interested ?? 0) - 1) : e.rsvp_interested,
      };
    }));

    try {
      const rsvpStatus = isSame ? 'declined' : action;
      await axios.post('/attendance/rsvp', {
        memberId: user.id,
        eventId,
        rsvpStatus,
      });
      const labels: Record<string, string> = { confirmed: 'Going ✓', interested: 'Interested ★', declined: 'Not going' };
      toast.success(isSame ? 'RSVP removed' : `RSVP: ${labels[action]}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'RSVP failed – please try again');
      fetchEvents();
    } finally {
      setRsvpLoading(p => ({ ...p, [eventId]: false }));
    }
  };

  const handleShare = (ev: Event) => {
    const text = `${ev.title} – ${smartDate(ev.event_date)}`;
    if (navigator.share) navigator.share({ title: ev.title, text }).catch(() => {});
    else { navigator.clipboard.writeText(text); toast.success('Copied to clipboard!'); }
  };

  return (
    <>
      {/* ─── Styles ─────────────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&display=swap');

        .mep-root { font-family: 'DM Sans', sans-serif; background: #F5F3EF; min-height: 100vh; }
        .mep-display { font-family: 'Syne', sans-serif; }

        /* Header grain overlay */
        .mep-header {
          background: linear-gradient(160deg, #041e0e 0%, #0D5128 55%, #156835 100%);
          position: relative; overflow: hidden;
        }
        .mep-header::before {
          content: '';
          position: absolute; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.07'/%3E%3C/svg%3E");
          opacity: 0.5; pointer-events: none;
        }

        /* Category scroll */
        .mep-cats { scrollbar-width: none; }
        .mep-cats::-webkit-scrollbar { display: none; }

        .mep-cat-pill {
          transition: transform 0.25s cubic-bezier(.34,1.56,.64,1), background 0.2s, color 0.2s;
          white-space: nowrap; cursor: pointer;
          border: 1.5px solid transparent;
        }
        .mep-cat-pill:hover { transform: translateY(-2px) scale(1.04); }

        /* Status tabs */
        .mep-tab { transition: all 0.2s ease; cursor: pointer; }

        /* Event card */
        .mep-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          transition: transform 0.35s cubic-bezier(.34,1.56,.64,1),
                      box-shadow 0.3s ease;
          box-shadow: 0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04);
        }
        .mep-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06);
        }

        /* Image zoom on hover */
        .mep-img-wrap { overflow: hidden; }
        .mep-img-wrap img,
        .mep-img-wrap .mep-placeholder {
          transition: transform 0.55s cubic-bezier(.25,.46,.45,.94);
        }
        .mep-card:hover .mep-img-wrap img,
        .mep-card:hover .mep-img-wrap .mep-placeholder {
          transform: scale(1.04);
        }

        /* Pulsing live dot */
        @keyframes livePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.7); }
          50%       { box-shadow: 0 0 0 5px rgba(34,197,94,0); }
        }
        .mep-live-dot { animation: livePulse 1.8s ease-in-out infinite; }

        /* Heart pop */
        @keyframes heartPop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.45); }
          70%  { transform: scale(0.9); }
          100% { transform: scale(1); }
        }
        .mep-heart-pop { animation: heartPop 0.38s cubic-bezier(.34,1.56,.64,1) forwards; }

        /* Going button */
        .mep-going-btn {
          transition: transform 0.22s cubic-bezier(.34,1.56,.64,1),
                      background 0.2s, color 0.2s;
        }
        .mep-going-btn:hover:not(:disabled) { transform: scale(1.04); }
        .mep-going-btn:active:not(:disabled) { transform: scale(0.94); }

        /* Shimmer skeleton */
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .mep-skeleton {
          background: linear-gradient(90deg, #ede8e0 25%, #e2dcd4 50%, #ede8e0 75%);
          background-size: 800px 100%;
          animation: shimmer 1.6s infinite linear;
        }

        /* Bottom nav */
        .mep-bottom-nav {
          position: fixed; bottom: 0; left: 0; right: 0;
          background: rgba(255,255,255,0.92);
          backdrop-filter: blur(16px);
          border-top: 1px solid rgba(0,0,0,0.06);
          z-index: 50;
        }
      `}</style>

      <div className="mep-root pb-24">
        {/* ─── Header ───────────────────────────────────────── */}
        <div className="mep-header sticky top-0 z-40">
          {/* Decorative orbs */}
          <div style={{
            position: 'absolute', right: -60, top: -60,
            width: 240, height: 240, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(61,170,117,0.22) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', left: -30, bottom: -40,
            width: 160, height: 160, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(26,122,74,0.25) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div className="relative max-w-xl mx-auto px-4 pt-5 pb-4">
            {/* Top row */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/member/dashboard')}
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.12)' }}
                >
                  <ChevronLeft size={18} color="white" />
                </button>
                <div>
                  <p className="mep-display text-green-300 text-[10px] font-semibold tracking-widest uppercase leading-none mb-0.5">
                    Hammad Foundation
                  </p>
                  <h1 className="mep-display text-white text-2xl font-bold leading-none">
                    Events Feed
                  </h1>
                </div>
              </div>
              <button
                onClick={() => setSearchOpen(s => !s)}
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: searchOpen ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)' }}
              >
                {searchOpen ? <X size={17} color="white" /> : <Search size={17} color="white" />}
              </button>
            </div>

            {/* Search bar */}
            {searchOpen && (
              <div
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl mb-3"
                style={{ background: 'rgba(255,255,255,0.13)', backdropFilter: 'blur(10px)' }}
              >
                <Search size={15} color="rgba(255,255,255,0.55)" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search events…"
                  className="flex-1 bg-transparent text-white placeholder-white/40 text-sm outline-none"
                />
                {search && (
                  <button onClick={() => setSearch('')}>
                    <X size={13} color="rgba(255,255,255,0.5)" />
                  </button>
                )}
              </div>
            )}

            {/* Status tabs */}
            {activeCategory === 'all' && (
              <div className="flex gap-2">
                {(['upcoming', 'ongoing', 'all', 'completed'] as const).map(s => (
                  <button
                    key={s}
                    className="mep-tab px-3.5 py-1 rounded-full text-xs font-semibold"
                    onClick={() => setActiveStatus(s)}
                    style={activeStatus === s ? {
                      background: 'white', color: '#0D5128',
                    } : {
                      background: 'rgba(255,255,255,0.13)', color: 'rgba(255,255,255,0.7)',
                    }}
                  >
                    {s === 'all' ? 'All' : s === 'ongoing' ? '🔴 Live' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ─── Category Scroll ──────────────────────────────── */}
        <div className="bg-white border-b border-stone-100 sticky top-[105px] z-30">
          <div
            ref={catScrollRef}
            className="mep-cats flex gap-2 px-4 py-3 overflow-x-auto max-w-xl mx-auto"
          >
            {CATEGORIES.map(cat => {
              const active = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  className="mep-cat-pill flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold"
                  style={active ? {
                    background: cat.accent,
                    color: 'white',
                    borderColor: cat.accent,
                  } : {
                    background: '#F5F3EF',
                    color: '#6b7280',
                    borderColor: '#e5e1db',
                  }}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    if (cat.id !== 'all' && cat.id !== 'upcoming_filter') setActiveStatus('all');
                    if (cat.id === 'all') setActiveStatus('upcoming');
                  }}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Feed ─────────────────────────────────────────── */}
        <div className="max-w-xl mx-auto px-4 pt-5">
          {loading ? (
            /* Skeleton */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="rounded-[20px] overflow-hidden" style={{ background: 'white' }}>
                  <div className="mep-skeleton" style={{ aspectRatio: '4/3' }} />
                  <div className="p-4 space-y-2.5">
                    <div className="mep-skeleton h-4 rounded-lg w-3/4" />
                    <div className="mep-skeleton h-3 rounded-lg w-1/2" />
                    <div className="mep-skeleton h-8 rounded-xl w-full mt-2" />
                  </div>
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            /* Empty state */
            <div className="text-center py-24">
              <div
                className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #f0fdf4, #d6f0e0)' }}
              >
                <Calendar size={34} color="#0D5128" strokeWidth={1.5} />
              </div>
              <h3 className="mep-display text-xl font-bold text-gray-800 mb-2">No events here</h3>
              <p className="text-gray-400 text-sm">Try a different category or check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {events.map(ev => (
                <EventFeedCard
                  key={ev.id}
                  event={ev}
                  onRSVP={handleRSVP}
                  onShare={handleShare}
                  rsvpLoading={!!rsvpLoading[ev.id]}
                  onOpenDetail={setSelectedEvent}
                />
              ))}
            </div>
          )}
        </div>

        {/* ─── Event Detail Sheet ───────────────────────────── */}
        {selectedEvent && (
          <EventDetailSheet
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onRSVP={(id, action) => { handleRSVP(id, action); setSelectedEvent(prev => prev ? { ...prev, my_rsvp: prev.my_rsvp === action ? null : action } : null); }}
            onShare={handleShare}
            rsvpLoading={!!rsvpLoading[selectedEvent.id]}
          />
        )}

        {/* ─── Bottom Nav ───────────────────────────────────── */}
        <nav className="mep-bottom-nav">
          <div className="max-w-xl mx-auto flex items-center justify-around py-3 px-4">
            <NavTab icon="🏠" label="Home" onClick={() => navigate('/member/dashboard')} active={false} />
            <NavTab icon="📅" label="Events" onClick={() => {}} active />
            <NavTab icon="👤" label="Profile" onClick={() => navigate('/member/profile')} active={false} />
            <NavTab icon="🔔" label="Alerts" onClick={() => navigate('/member/notifications')} active={false} />
          </div>
        </nav>
      </div>
    </>
  );
};

// ─── Nav Tab ─────────────────────────────────────────────────────────────────

const NavTab: React.FC<{ icon: string; label: string; onClick: () => void; active: boolean }> = ({
  icon, label, onClick, active,
}) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-0.5 px-4 py-1 rounded-xl transition-all"
    style={{ color: active ? '#0D5128' : '#9ca3af', background: 'transparent', border: 'none', cursor: 'pointer' }}
  >
    <span style={{ fontSize: 20, lineHeight: 1 }}>{icon}</span>
    <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, fontFamily: 'DM Sans, sans-serif' }}>{label}</span>
    {active && (
      <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#0D5128', marginTop: 1 }} />
    )}
  </button>
);

// ─── Event Feed Card ─────────────────────────────────────────────────────────

interface CardProps {
  event: Event;
  onRSVP: (id: number, action: 'confirmed' | 'interested' | 'declined') => void;
  onShare: (ev: Event) => void;
  rsvpLoading: boolean;
  onOpenDetail: (ev: Event) => void;
}

const EventFeedCard: React.FC<CardProps> = ({ event, onRSVP, onShare, rsvpLoading, onOpenDetail }) => {
  const [heartAnim, setHeartAnim] = useState(false);
  const statusCfg = STATUS_CFG[event.status] ?? STATUS_CFG.completed;
  const catCfg = CATEGORIES.find(c => c.id === event.category);
  const gradient = CAT_GRADIENTS[event.category] ?? CAT_GRADIENTS.default;
  const isLiked    = event.my_rsvp === 'interested';
  const isGoing    = event.my_rsvp === 'confirmed';
  const isDeclined = event.my_rsvp === 'declined';
  const isPast     = event.status === 'completed';

  const handleHeart = () => {
    if (isPast) return;
    setHeartAnim(true);
    setTimeout(() => setHeartAnim(false), 400);
    onRSVP(event.id, 'interested');
  };

  return (
    <article className="mep-card">
      {/* ── Image / Placeholder ── */}
      <div
        className="mep-img-wrap relative"
        style={event.brochure_url
          ? { background: '#000', cursor: 'pointer' }
          : { aspectRatio: '4/3', cursor: 'pointer' }
        }
        onClick={() => onOpenDetail(event)}
      >
        {event.brochure_url ? (
          <img
            src={event.brochure_url}
            alt={event.title}
            style={{ width: '100%', display: 'block', maxHeight: 380, objectFit: 'contain' }}
          />
        ) : (
          <div
            className="mep-placeholder w-full h-full flex items-center justify-center"
            style={{ background: gradient }}
          >
            {/* Soft radial highlights */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.12) 0%, transparent 55%), radial-gradient(ellipse at 75% 80%, rgba(255,255,255,0.08) 0%, transparent 50%)',
            }} />
            <span
              className="mep-display select-none"
              style={{ color: 'rgba(255,255,255,0.13)', fontSize: 90, fontWeight: 800, lineHeight: 1, zIndex: 1 }}
            >
              {event.title.charAt(0)}
            </span>
          </div>
        )}

        {/* Bottom gradient */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 45%, transparent 100%)',
        }} />

        {/* Status badge – top left */}
        <div style={{
          position: 'absolute', top: 12, left: 12,
          display: 'flex', alignItems: 'center', gap: 5,
          background: statusCfg.bg,
          backdropFilter: 'blur(10px)',
          borderRadius: 999,
          padding: '4px 9px',
          border: `1px solid ${statusCfg.color}28`,
        }}>
          <div
            style={{ width: 6, height: 6, borderRadius: '50%', background: statusCfg.color, flexShrink: 0 }}
            className={statusCfg.glow ? 'mep-live-dot' : ''}
          />
          <span style={{ color: statusCfg.color, fontSize: 10.5, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>
            {statusCfg.label}
          </span>
        </div>

        {/* Points – top right */}
        <div style={{
          position: 'absolute', top: 12, right: 12,
          display: 'flex', alignItems: 'center', gap: 4,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(8px)',
          borderRadius: 999, padding: '4px 9px',
        }}>
          <Zap size={11} color="#FBBF24" fill="#FBBF24" />
          <span style={{ color: 'white', fontSize: 11, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>
            {event.points} pts
          </span>
        </div>

        {/* Category chip – bottom left */}
        {catCfg && (
          <div style={{ position: 'absolute', bottom: 12, left: 12 }}>
            <span style={{
              background: catCfg.accent,
              color: 'white',
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              padding: '3px 8px',
              borderRadius: 6,
              fontFamily: 'Syne, sans-serif',
            }}>
              {catCfg.emoji} {catCfg.label}
            </span>
          </div>
        )}

        {/* Date chip – bottom right */}
        <div style={{ position: 'absolute', bottom: 12, right: 12 }}>
          <span style={{
            background: 'rgba(0,0,0,0.42)',
            backdropFilter: 'blur(6px)',
            color: 'rgba(255,255,255,0.92)',
            fontSize: 10, fontWeight: 600,
            padding: '3px 8px', borderRadius: 6,
          }}>
            {smartDate(event.event_date)}
          </span>
        </div>
      </div>

      {/* ── Card Body ── */}
      <div className="px-4 pt-3 pb-3">
        {/* Title */}
        <h3 className="mep-display font-bold text-gray-900 leading-snug mb-2 line-clamp-2"
            style={{ fontSize: 15, cursor: 'pointer' }}
            onClick={() => onOpenDetail(event)}>
          {event.title}
        </h3>

        {/* Description */}
        {event.description && (
          <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.55, marginBottom: 8 }}>
            {event.description}
          </p>
        )}

        {/* Meta row */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
          {event.event_time && (
            <div className="flex items-center gap-1 text-gray-400">
              <Clock size={11} />
              <span style={{ fontSize: 11 }}>{event.event_time}</span>
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-1 text-gray-400">
              <MapPin size={11} />
              <span style={{ fontSize: 11 }} className="truncate max-w-[140px]">{event.location}</span>
            </div>
          )}
          {(event.rsvp_confirmed ?? 0) > 0 && (
            <div className="flex items-center gap-1 text-gray-400">
              <Users size={11} />
              <span style={{ fontSize: 11 }}>{event.rsvp_confirmed} going</span>
            </div>
          )}
        </div>

        {/* ── Action row ── */}
        <div className="flex items-center gap-2">
          {/* Heart / Interested */}
          <button
            onClick={handleHeart}
            disabled={isPast || rsvpLoading}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl transition-colors ${isPast ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
            style={{ background: isLiked ? 'rgba(244,63,94,0.10)' : '#F5F3EF', border: 'none' }}
          >
            <Heart
              size={16}
              fill={isLiked ? '#f43f5e' : 'none'}
              color={isLiked ? '#f43f5e' : '#9ca3af'}
              className={heartAnim ? 'mep-heart-pop' : ''}
            />
            {(event.rsvp_interested ?? 0) > 0 && (
              <span style={{ fontSize: 11, fontWeight: 600, color: isLiked ? '#f43f5e' : '#9ca3af' }}>
                {event.rsvp_interested}
              </span>
            )}
          </button>

          {/* Going button */}
          <button
            onClick={() => !isPast && onRSVP(event.id, 'confirmed')}
            disabled={isPast || rsvpLoading}
            className={`mep-going-btn flex-1 py-2 rounded-xl text-xs font-bold ${isPast ? 'opacity-40 cursor-not-allowed' : ''}`}
            style={isGoing ? {
              background: 'linear-gradient(135deg, #0D5128 0%, #1a7a4a 100%)',
              color: 'white', border: 'none',
            } : {
              background: '#F0FDF4',
              color: '#0D5128', border: 'none',
            }}
          >
            {isGoing ? '✓ Going' : 'Going?'}
          </button>

          {/* Can't go */}
          {!isPast && (
            <button
              onClick={() => onRSVP(event.id, 'declined')}
              disabled={rsvpLoading}
              title="Can't attend"
              className="flex items-center justify-center w-9 h-9 rounded-xl transition-colors"
              style={{ background: isDeclined ? 'rgba(100,116,139,0.12)' : '#F5F3EF', border: 'none', cursor: 'pointer' }}
            >
              <Slash size={14} color={isDeclined ? '#64748b' : '#d1d5db'} />
            </button>
          )}

          {/* Share */}
          <button
            onClick={() => onShare(event)}
            className="flex items-center justify-center w-9 h-9 rounded-xl transition-colors"
            style={{ background: '#F5F3EF', border: 'none', cursor: 'pointer' }}
          >
            <Share2 size={14} color="#9ca3af" />
          </button>
        </div>
      </div>
    </article>
  );
};

// ─── Event Detail Sheet ──────────────────────────────────────────────────────

interface SheetProps {
  event: Event;
  onClose: () => void;
  onRSVP: (id: number, action: 'confirmed' | 'interested' | 'declined') => void;
  onShare: (ev: Event) => void;
  rsvpLoading: boolean;
}

const EventDetailSheet: React.FC<SheetProps> = ({ event, onClose, onRSVP, onShare, rsvpLoading }) => {
  const statusCfg = STATUS_CFG[event.status] ?? STATUS_CFG.completed;
  const catCfg    = CATEGORIES.find(c => c.id === event.category);
  const gradient  = CAT_GRADIENTS[event.category] ?? CAT_GRADIENTS.default;
  const isGoing    = event.my_rsvp === 'confirmed';
  const isLiked    = event.my_rsvp === 'interested';
  const isDeclined = event.my_rsvp === 'declined';
  const isPast     = event.status === 'completed';

  // Close on backdrop click
  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      <style>{`
        @keyframes sheetSlideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .mep-sheet {
          animation: sheetSlideUp 0.36s cubic-bezier(.32,1.12,.64,1) forwards;
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={handleBackdrop}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 100,
          display: 'flex', alignItems: 'flex-end',
        }}
      >
        {/* Sheet */}
        <div
          className="mep-sheet"
          style={{
            width: '100%',
            maxHeight: '90vh',
            background: 'white',
            borderRadius: '24px 24px 0 0',
            overflowY: 'auto',
            paddingBottom: 40,
          }}
        >
          {/* Drag handle */}
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, marginBottom: 4 }}>
            <div style={{ width: 36, height: 4, borderRadius: 99, background: '#e5e7eb' }} />
          </div>

          {/* Hero image */}
          <div style={{ position: 'relative', width: '100%', background: event.brochure_url ? '#000' : undefined, overflow: 'hidden' }}>
            {event.brochure_url ? (
              <img src={event.brochure_url} alt={event.title}
                style={{ width: '100%', display: 'block', objectFit: 'contain' }} />
            ) : (
              <div style={{ width: '100%', aspectRatio: '16/9', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: 'rgba(255,255,255,0.14)', fontSize: 120, fontWeight: 800, fontFamily: 'Syne, sans-serif', lineHeight: 1 }}>
                  {event.title.charAt(0)}
                </span>
              </div>
            )}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 60%)', pointerEvents: 'none' }} />

            {/* Status badge */}
            <div style={{
              position: 'absolute', top: 14, left: 14,
              display: 'flex', alignItems: 'center', gap: 5,
              background: statusCfg.bg, backdropFilter: 'blur(10px)',
              borderRadius: 999, padding: '4px 10px',
              border: `1px solid ${statusCfg.color}28`,
            }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusCfg.color }}
                className={statusCfg.glow ? 'mep-live-dot' : ''} />
              <span style={{ color: statusCfg.color, fontSize: 10.5, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>
                {statusCfg.label}
              </span>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              style={{
                position: 'absolute', top: 14, right: 14,
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(0,0,0,0.45)', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={16} color="white" />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 20px 8px' }}>
            {/* Category + points */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              {catCfg && (
                <span style={{
                  background: catCfg.accent, color: 'white',
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                  textTransform: 'uppercase', padding: '3px 10px', borderRadius: 6,
                  fontFamily: 'Syne, sans-serif',
                }}>
                  {catCfg.emoji} {catCfg.label}
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#FEF3C7', borderRadius: 8, padding: '4px 10px' }}>
                <Award size={13} color="#b45309" />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#b45309', fontFamily: 'Syne, sans-serif' }}>
                  {event.points} pts
                </span>
              </div>
            </div>

            {/* Title */}
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22, color: '#111827', lineHeight: 1.25, marginBottom: 14 }}>
              {event.title}
            </h2>

            {/* Details grid */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Calendar size={16} color="#3b82f6" />
                </div>
                <div>
                  <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 1 }}>Date</p>
                  <p style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>
                    {(() => { try { return format(parseISO(event.event_date), 'EEEE, MMMM d, yyyy'); } catch { return event.event_date; } })()}
                  </p>
                </div>
              </div>

              {event.event_time && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Clock size={16} color="#16a34a" />
                  </div>
                  <div>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 1 }}>Time</p>
                    <p style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>{event.event_time}</p>
                  </div>
                </div>
              )}

              {event.location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: '#FFF1F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <MapPin size={16} color="#e11d48" />
                  </div>
                  <div>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 1 }}>Location</p>
                    <p style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>{event.location}</p>
                  </div>
                </div>
              )}

              {event.capacity && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Users size={16} color="#7c3aed" />
                  </div>
                  <div>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 1 }}>Capacity</p>
                    <p style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>
                      {(event.rsvp_confirmed ?? 0)} / {event.capacity} confirmed
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>About</p>
                <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.65 }}>{event.description}</p>
              </div>
            )}

            {/* RSVP stats */}
            {((event.rsvp_confirmed ?? 0) > 0 || (event.rsvp_interested ?? 0) > 0) && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {(event.rsvp_confirmed ?? 0) > 0 && (
                  <div style={{ flex: 1, background: '#F0FDF4', borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
                    <p style={{ fontSize: 18, fontWeight: 800, color: '#16a34a', fontFamily: 'Syne, sans-serif' }}>{event.rsvp_confirmed}</p>
                    <p style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>Going</p>
                  </div>
                )}
                {(event.rsvp_interested ?? 0) > 0 && (
                  <div style={{ flex: 1, background: '#FFF1F2', borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
                    <p style={{ fontSize: 18, fontWeight: 800, color: '#e11d48', fontFamily: 'Syne, sans-serif' }}>{event.rsvp_interested}</p>
                    <p style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>Interested</p>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            {!isPast && (
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => onRSVP(event.id, 'confirmed')}
                  disabled={rsvpLoading}
                  style={{
                    flex: 1, padding: '13px', borderRadius: 14, border: 'none', cursor: 'pointer',
                    fontWeight: 700, fontSize: 14, fontFamily: 'Syne, sans-serif',
                    background: isGoing ? 'linear-gradient(135deg, #0D5128 0%, #1a7a4a 100%)' : '#F0FDF4',
                    color: isGoing ? 'white' : '#0D5128',
                    transition: 'all 0.2s',
                  }}
                >
                  {isGoing ? '✓ Going' : 'Going?'}
                </button>

                <button
                  onClick={() => onRSVP(event.id, 'interested')}
                  disabled={rsvpLoading}
                  style={{
                    width: 48, height: 48, borderRadius: 14, border: 'none', cursor: 'pointer',
                    background: isLiked ? 'rgba(244,63,94,0.10)' : '#F5F3EF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Heart size={18} fill={isLiked ? '#f43f5e' : 'none'} color={isLiked ? '#f43f5e' : '#9ca3af'} />
                </button>

                <button
                  onClick={() => onRSVP(event.id, 'declined')}
                  disabled={rsvpLoading}
                  style={{
                    width: 48, height: 48, borderRadius: 14, border: 'none', cursor: 'pointer',
                    background: isDeclined ? 'rgba(100,116,139,0.12)' : '#F5F3EF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Slash size={16} color={isDeclined ? '#64748b' : '#d1d5db'} />
                </button>

                <button
                  onClick={() => onShare(event)}
                  style={{
                    width: 48, height: 48, borderRadius: 14, border: 'none', cursor: 'pointer',
                    background: '#F5F3EF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Share2 size={16} color="#9ca3af" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MemberEventsPage;
